import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, RefreshCw, Smartphone, ShieldAlert, ChevronRight, Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast/Toast';
import {
  isCallLogAvailable, checkCallLogPermission, requestCallLogPermission,
  getDeviceCallLog, CALL_TYPE, DeviceCall,
} from '../services/callLog';

/**
 * Day-wise call summary — every day the handset still holds, not a window.
 *
 * The Call Log screen reads a bounded slice (7 / 30 days) because it draws one
 * row per call; a summary is a few numbers per day, so it reads the lot. The
 * ceiling here is the phone, not this page: Android prunes its own call log,
 * and days it has dropped cannot be recovered by any app. The header states
 * the oldest call actually on the device so that limit is visible rather than
 * looking like a bug in this report.
 */

// Deliberately far above any real handset. A busy business phone holds a few
// thousand calls; this is "everything" without being literally unbounded.
const READ_LIMIT = 200000;

interface DayRow {
  key: string;
  day: number;          // day of month
  label: string;
  incoming: number;
  /** Outgoing calls that were actually answered. */
  outgoing: number;
  /** Dialled but never picked up — an outgoing call of no duration. */
  noAnswer: number;
  missed: number;
  other: number;
  total: number;
}

interface MonthRow {
  key: string;          // YYYY-MM
  label: string;        // "September 2026"
  days: DayRow[];
  incoming: number;
  outgoing: number;
  noAnswer: number;
  missed: number;
  total: number;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Local calendar day, as YYYY-MM-DD — a call at 11pm belongs to that evening. */
function dayKey(ms: number) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function longDate(ms: number) {
  return new Date(ms).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
}

const CallSummary: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();

  const [permission, setPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [calls, setCalls] = useState<DeviceCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Months start collapsed except the newest, so a year of history opens fast.
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getDeviceCallLog(READ_LIMIT, 0);
      setCalls(rows);
      setLoaded(true);
    } catch (e: any) {
      showError('Could not read call log', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (!isCallLogAvailable) return;
    checkCallLogPermission().then(granted => {
      setPermission(granted ? 'granted' : 'denied');
      if (granted) load();
    });
    // Mount only: re-reading the whole log on every render would be brutal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askPermission = async () => {
    const granted = await requestCallLogPermission();
    setPermission(granted ? 'granted' : 'denied');
    if (granted) load();
  };

  // Every day, newest first, grouped under its month.
  const months = useMemo(() => {
    const monthList: MonthRow[] = [];
    const byMonth: Record<string, MonthRow> = {};
    const byDay: Record<string, DayRow> = {};

    // The device hands back newest-first, so both lists come out in order.
    for (const c of calls) {
      const d = new Date(c.date);
      const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      let month = byMonth[mKey];
      if (!month) {
        month = {
          key: mKey,
          label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
          days: [], incoming: 0, outgoing: 0, noAnswer: 0, missed: 0, total: 0,
        };
        byMonth[mKey] = month;
        monthList.push(month);
      }

      const dKey = dayKey(c.date);
      let day = byDay[dKey];
      if (!day) {
        day = {
          key: dKey,
          day: d.getDate(),
          label: String(d.getDate()).padStart(2, '0') + ' ' + d.toLocaleDateString([], { weekday: 'short' }),
          incoming: 0, outgoing: 0, noAnswer: 0, missed: 0, other: 0, total: 0,
        };
        byDay[dKey] = day;
        month.days.push(day);
      }

      day.total++;
      month.total++;
      if (c.type === CALL_TYPE.INCOMING) { day.incoming++; month.incoming++; }
      else if (c.type === CALL_TYPE.OUTGOING) {
        if (c.duration > 0) { day.outgoing++; month.outgoing++; }
        else { day.noAnswer++; month.noAnswer++; }
      }
      else if (c.type === CALL_TYPE.MISSED) { day.missed++; month.missed++; }
      else day.other++;
    }
    return monthList;
  }, [calls]);

  const totals = useMemo(
    () => months.reduce(
      (t, m) => ({
        incoming: t.incoming + m.incoming,
        outgoing: t.outgoing + m.outgoing,
        noAnswer: t.noAnswer + m.noAnswer,
        missed: t.missed + m.missed,
        total: t.total + m.total,
        days: t.days + m.days.length,
      }),
      { incoming: 0, outgoing: 0, noAnswer: 0, missed: 0, total: 0, days: 0 },
    ),
    [months],
  );

  // How far back the handset itself goes. Calls arrive newest-first.
  const oldest = calls.length ? calls[calls.length - 1].date : null;

  // The newest month is open on arrival; the rest are one tap away.
  useEffect(() => {
    if (months.length) setOpen(o => (Object.keys(o).length ? o : { [months[0].key]: true }));
  }, [months]);

  if (!isCallLogAvailable) {
    return (
      <div className="py-6 max-w-md mx-auto px-6 text-center">
        <Smartphone className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <h2 className="text-base font-semibold text-gray-800">Call Summary is mobile only</h2>
        <p className="text-sm text-gray-500 mt-1">
          This report counts the call history stored on the handset, so it works only in the
          ABS Cloud Android app and not in a desktop browser.
        </p>
      </div>
    );
  }

  if (permission !== 'granted') {
    return (
      <div className="py-6 max-w-md mx-auto px-6 text-center">
        <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-base font-semibold text-gray-800">Allow call log access</h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          The summary is counted on this device only. Nothing is uploaded to the server.
        </p>
        <button onClick={askPermission} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">
          Allow access
        </button>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="flex items-center gap-2 px-3 py-2">
          <CalendarDays className="h-5 w-5 text-indigo-600" />
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-gray-800 leading-tight">Day-wise Call Summary</h1>
            <p className="text-[11px] text-gray-500 truncate">
              {loading
                ? 'Reading the whole call log…'
                : `${totals.total} calls over ${totals.days} day(s)`}
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-50"
            aria-label="Re-read the call log"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase text-gray-400 border-t border-gray-100">
          <span className="flex-1">Day</span>
          <span className="w-10 text-right">In</span>
          <span className="w-10 text-right">Out</span>
          <span className="w-12 text-right text-amber-600">No ans</span>
          <span className="w-10 text-right">Miss</span>
          <span className="w-10 text-right">Total</span>
        </div>
      </div>

      {/* Why the report starts where it does. Android keeps a limited call
          history and prunes the rest; that is the phone's doing, not this
          report's, and saying so is better than leaving it to be guessed. */}
      {loaded && oldest !== null && (
        <div className="mx-3 mt-3 flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            The oldest call on this phone is from <strong>{longDate(oldest)}</strong>. Android keeps
            only a limited call history and deletes what is older, so anything before that date is
            no longer on the device and cannot be shown here.
          </p>
        </div>
      )}

      {/* "Out" and "No ans" both mean a call this phone placed, so say which
          is which rather than leaving the two columns to be worked out. */}
      {months.length > 0 && (
        <p className="px-4 mt-3 text-[11px] text-gray-500 leading-relaxed">
          <strong>Out</strong> is a call you made that was answered.{' '}
          <strong className="text-amber-700">No ans</strong> is one you made that was never
          picked up — it has no call duration. Tap that figure to see those calls.
        </p>
      )}

      {loaded && months.length === 0 && (
        <div className="px-6 py-10 text-center text-sm text-gray-400">No calls on this phone.</div>
      )}

      {months.map(m => (
        <div key={m.key} className="mx-3 mt-3 rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setOpen(o => ({ ...o, [m.key]: !o[m.key] }))}
            className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-left"
          >
            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${open[m.key] ? 'rotate-90' : ''}`} />
            <span className="flex-1 text-sm font-bold text-gray-800">{m.label}</span>
            <span className="w-10 text-right text-sm tabular-nums text-emerald-700 font-semibold">{m.incoming}</span>
            <span className="w-10 text-right text-sm tabular-nums text-blue-700 font-semibold">{m.outgoing}</span>
            <span className="w-12 text-right text-sm tabular-nums text-amber-700 font-semibold">{m.noAnswer}</span>
            <span className="w-10 text-right text-sm tabular-nums text-red-600 font-semibold">{m.missed}</span>
            <span className="w-10 text-right text-sm tabular-nums text-gray-900 font-bold">{m.total}</span>
          </button>

          {open[m.key] && (
            <div className="divide-y divide-gray-100">
              {m.days.map(d => (
                // Two controls, not one: the row opens the day, while the
                // "No ans" figure opens that day already filtered to the calls
                // that were dialled and never picked up. A button inside a
                // button is invalid markup, hence the div.
                <div key={d.key} className="flex items-center gap-2 px-3 text-sm bg-white">
                  <button
                    onClick={() => navigate(`/call-log?day=${d.key}`)}
                    className="flex-1 flex items-center gap-2 py-2 text-left min-w-0"
                  >
                    <span className="flex-1 font-medium text-gray-700 truncate">{d.label}</span>
                    <span className="w-10 text-right tabular-nums text-emerald-600">{d.incoming || '—'}</span>
                    <span className="w-10 text-right tabular-nums text-blue-600">{d.outgoing || '—'}</span>
                  </button>
                  <button
                    onClick={() => navigate(`/call-log?day=${d.key}&type=noanswer`)}
                    className={`w-12 py-2 text-right tabular-nums font-semibold ${
                      d.noAnswer ? 'text-amber-700 underline' : 'text-gray-300'
                    }`}
                    title="Calls you made that were not received"
                  >
                    {d.noAnswer || '—'}
                  </button>
                  <button
                    onClick={() => navigate(`/call-log?day=${d.key}`)}
                    className="flex items-center gap-2 py-2"
                  >
                    <span className="w-10 text-right tabular-nums text-red-500">{d.missed || '—'}</span>
                    <span className="w-10 text-right tabular-nums text-gray-800 font-bold">{d.total}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {months.length > 0 && (
        <div className="mx-3 mt-3 flex items-center gap-2 px-3 py-3 rounded-xl bg-gray-800 text-white text-sm">
          <span className="flex-1 font-bold">Grand total</span>
          <span className="w-10 text-right tabular-nums font-bold">{totals.incoming}</span>
          <span className="w-10 text-right tabular-nums font-bold">{totals.outgoing}</span>
          <span className="w-12 text-right tabular-nums font-bold text-amber-300">{totals.noAnswer}</span>
          <span className="w-10 text-right tabular-nums font-bold">{totals.missed}</span>
          <span className="w-10 text-right tabular-nums font-bold">{totals.total}</span>
        </div>
      )}
    </div>
  );
};

export default CallSummary;
